/**
 * Description: Cached DOM scan for platforms and text targets; uses injected host only (no raw window/document reads in core logic except via host).
 * Usage: new Environment(host); init(); update() each frame when dirty.
 */
export class Environment {
  constructor(host) {
    this._host = host;
    this._platforms = [];
    this._dirty = true;
    this._MIN_WIDTH = 60;
    this._SELECTORS = [
      'header',
      'nav',
      'footer',
      'aside',
      'section',
      'article',
      '.card',
      '.widget',
      '.sidebar',
      '.wp-block-image',
      '.wp-block-button',
      '.wp-block-group',
      '.wp-block-cover',
      '.wp-block-columns',
      '.entry-content img',
      '[class*="hero"]',
      '[class*="banner"]',
      'table',
      '.woocommerce-product-gallery',
    ].join(',');
  }

  init() {
    this.scan();
    const win = this._host.window;
    win.addEventListener('scroll', this._markDirty, { passive: true });
    win.addEventListener('resize', this._markDirty, { passive: true });
  }

  destroy() {
    const win = this._host.window;
    win.removeEventListener('scroll', this._markDirty);
    win.removeEventListener('resize', this._markDirty);
  }

  _markDirty = () => {
    this._dirty = true;
  };

  scan() {
    this._platforms = [];
    const win = this._host.window;
    const doc = this._host.document;
    const vw = win.innerWidth;
    const vh = win.innerHeight;

    this._platforms.push({ left: 0, right: vw, y: vh });

    let elements;
    try {
      elements = doc.querySelectorAll(this._SELECTORS);
    } catch {
      this._dirty = false;
      return;
    }

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (el.offsetParent === null && el.tagName !== 'BODY' && el.tagName !== 'HTML') continue;

      const rect = el.getBoundingClientRect();
      if (rect.width < this._MIN_WIDTH) continue;
      if (rect.height < 10) continue;
      if (rect.bottom < -50 || rect.top > vh + 50) continue;
      if (rect.right < 0 || rect.left > vw) continue;

      this._platforms.push({
        left: Math.max(0, rect.left),
        right: Math.min(vw, rect.right),
        y: rect.top,
      });
    }

    this._platforms.sort((a, b) => a.y - b.y);
    this._dirty = false;
  }

  update() {
    if (this._dirty) this.scan();
  }

  getPlatforms() {
    return this._platforms;
  }

  getFloorY() {
    return this._host.window.innerHeight;
  }

  getViewport() {
    const win = this._host.window;
    return { width: win.innerWidth, height: win.innerHeight };
  }

  findPlatformsNear(x, y, range) {
    const results = [];
    for (let i = 0; i < this._platforms.length; i++) {
      const p = this._platforms[i];
      if (Math.abs(p.y - y) > range) continue;
      if (x < p.left || x > p.right) continue;
      results.push(p);
    }
    return results;
  }

  findTextElements() {
    const doc = this._host.document;
    const win = this._host.window;
    const selectors = 'h1,h2,h3,h4,h5,h6,p,.entry-title,blockquote,li,span,a,label';
    const results = [];
    const vh = win.innerHeight;
    const vw = win.innerWidth;
    let elements;
    try {
      elements = doc.querySelectorAll(selectors);
    } catch {
      return results;
    }

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (el.closest('#ndp-pet-container')) continue;
      if (el.offsetParent === null && el.tagName !== 'BODY') continue;
      const rect = el.getBoundingClientRect();
      if (rect.width < 20 || rect.height < 10) continue;
      if (rect.bottom < 0 || rect.top > vh) continue;
      if (rect.right < 0 || rect.left > vw) continue;
      results.push({
        x: rect.left,
        y: rect.bottom,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
      });
    }
    return results;
  }
}
